import { VegaChartMasterColor } from '@asset-metrix/portal-gateway-client-js';
import { Signal, Spec } from 'vega';

import { extractBindingsFromSignals } from './inputBinding/extraction';
import { ChartColor } from './chartColors';

const RESERVED_SIGNAL_NAMES = new Set([
  'width',
  'height',
  'masterColorKey',
  'masterColorValue',
  'textColor',
  'primaryColor',
  'secondaryColor',
  'isZoomable',
]);

function filterExistingSignals(signals: Spec['signals']): Signal[] {
  if (signals == null) {
    return [];
  }

  return signals.filter(({ name }) => !RESERVED_SIGNAL_NAMES.has(name));
}

export type PortalVegaOptions = {
  masterColors: VegaChartMasterColor[];
  textColor: ChartColor;
  primaryColor: ChartColor;
  secondaryColor: ChartColor;
};

export function prepareSpecs(
  spec: Spec,
  { masterColors, textColor, primaryColor, secondaryColor }: PortalVegaOptions
) {
  const baseSpec: Spec = {
    ...spec,
    width: undefined,
    height: undefined,

    signals: [
      ...filterExistingSignals(spec.signals),
      ...masterColors,
      {
        name: 'width',
        init: 'isFinite(containerSize()[0]) ? containerSize()[0] : 200',
        on: [
          {
            update: 'isFinite(containerSize()[0]) ? containerSize()[0] : 200',
            events: 'window:resizevegachart',
          },
        ],
      },
      {
        name: 'height',
        init: 'isFinite(containerSize()[1]) ? containerSize()[1] : 200',
        on: [
          {
            update: 'isFinite(containerSize()[1]) ? containerSize()[1] : 200',
            events: 'window:resizevegachart',
          },
        ],
      },
      {
        name: 'textColor',
        value: textColor,
      },
      {
        name: 'primaryColor',
        value: primaryColor,
      },
      {
        name: 'secondaryColor',
        value: secondaryColor,
      },
    ],
  };

  // Ensure data source stubs are there
  const specData = baseSpec.data ?? [];
  for (const dataSourceName of ['labelData', 'rawData']) {
    if (!specData.some((d) => d.name === dataSourceName)) {
      specData.push({
        name: dataSourceName,
      });
    }
  }

  baseSpec.data = specData;

  const { bindings, spec: baseSpecWithoutBindings } =
    extractBindingsFromSignals(baseSpec);

  const fullscreenSpec: Spec = {
    ...baseSpecWithoutBindings,
    signals: [
      ...(baseSpecWithoutBindings.signals ?? []),
      {
        name: 'isZoomable',
        value: true,
      },
    ],
  };

  const nonFullscreenSpec: Spec = {
    ...baseSpecWithoutBindings,
    signals: [
      ...(baseSpecWithoutBindings.signals ?? []),
      {
        name: 'isZoomable',
        value: false,
      },
    ],
  };

  return { bindings, nonFullscreenSpec, fullscreenSpec };
}
